// apps/admin/src/components/CompanyOnboardingWizard.tsx

import React, { useState, useEffect } from 'react';
import { db, functions, storage } from '../firebaseConfig';
import { httpsCallable } from 'firebase/functions';
import { ref, uploadBytesResumable } from "firebase/storage";
import { collection, query, where, getDocs } from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';
import type { OnboardPayloadDTO, PaymentConfiguration, UploadMode } from "@/types/onboarding";
import { CreditCard, DollarSign, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import OnboardingDataUploader from './OnboardingDataUploader';

// --- Type Definitions ---
// Company interface removed - not used in this component

// Add these interfaces AFTER the imports but BEFORE the component
interface CompanyDetails {
  companyName: string;
  acl: string;
  website: string;
  phone: string;
  address: string;
}

interface Contact {
  firstName: string;
  lastName: string;
  mobile: string;
  email: string;
}

interface Office {
  name: string;
  isHeadOffice: boolean;
  entityCode?: string;
}

interface User {
  firstName: string;
  lastName: string;
  email: string;
  role: 'super_admin' | 'administrator' | 'office_admin' | 'broker';
}

interface FileTemplate {
  id: string;
  templateName: string;
  name?: string; // getTemplates may return 'name' instead
  aggregator?: string;
}

// --- Helper Function ---
async function isZipFile(file: File): Promise<boolean> {
  // ZIP signatures: PK 03 04 (normal), PK 05 06 (empty), PK 07 08 (spanned)
  if (file.size < 4) return false;
  const buf = new Uint8Array(await file.slice(0, 4).arrayBuffer());
  return (
    buf[0] === 0x50 && buf[1] === 0x4b &&
    ((buf[2] === 0x03 && buf[3] === 0x04) ||
     (buf[2] === 0x05 && buf[3] === 0x06) ||
     (buf[2] === 0x07 && buf[3] === 0x08))
  );
}

const CompanyOnboardingWizard: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [companyDetails, setCompanyDetails] = useState<CompanyDetails>({ 
    companyName: '', 
    acl: '', 
    website: '', 
    phone: '', 
    address: '' 
  });
  const [mainContact, setMainContact] = useState<Contact>({ 
    firstName: '', 
    lastName: '', 
    mobile: '', 
    email: '' 
  });
  const [offices, setOffices] = useState<Office[]>([{ name: '', isHeadOffice: true }]);
  const [users, setUsers] = useState<User[]>([]);
  const [fileTemplates, setFileTemplates] = useState<FileTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [zipFile, setZipFile] = useState<File | null>(null);
  const [rctiFile, setRctiFile] = useState<File | null>(null);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [uploadMode, setUploadMode] = useState<UploadMode>('paired');
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isMultiOffice, setIsMultiOffice] = useState(false);
  const [additionalOffices, setAdditionalOffices] = useState<{
    name: string;
    entityCode: string;
    isHeadOffice: boolean;
  }[]>([]);

  // Payment configuration state - properly typed
  const [paymentConfig, setPaymentConfig] = useState<PaymentConfiguration>({
    isManualPayment: true,
    subscriptionPlan: 'monthly',
    onboardingFee: 150000, // $1500 in cents
    paymentMethod: 'bank_transfer',
    invoiceNumber: '',
    paymentReceived: false
  });

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        // Use getTemplates cloud function to get all available real templates.
        // Exclude demo/sample templates — onboarding auto-selects templates[0] as
        // the default, which must never be a parser-less demo template.
        const getTemplatesFn = httpsCallable(functions, 'getTemplates');
        const result = await getTemplatesFn({ excludeDemo: true }) as any;

        if (result.data.success) {
          const fetchedTemplates = result.data.templates.map((t: any) => ({
            id: t.id,
            templateName: t.name || t.templateName,
            name: t.name || t.templateName,
            aggregator: t.aggregator
          } as FileTemplate));

          setFileTemplates(fetchedTemplates);

          // Default to first available template (no magic constants)
          if (fetchedTemplates.length > 0) {
            setSelectedTemplateId(fetchedTemplates[0].id);
            console.log('[Onboarding] Template auto-selected:', {
              id: fetchedTemplates[0].id,
              name: fetchedTemplates[0].templateName,
              totalAvailable: fetchedTemplates.length
            });
          } else {
            console.warn('[Onboarding] No file templates available');
            setError('No file templates found. Please contact support to set up templates.');
          }
        }
      } catch (_e) {
        console.error('[Onboarding] Template fetch failed:', _e);
        setError("Could not load file templates from the database.");
      }
    };
    fetchTemplates();
  }, []);

  const handleNextStep = () => setStep(prev => prev + 1);
  const handlePrevStep = () => setStep(prev => prev - 1);

  const addUser = () => {
    const defaultRole = users.length === 0 ? 'super_admin' : 'broker';
    setUsers([...users, { firstName: '', lastName: '', email: '', role: defaultRole }]);
  };

  const updateUser = (index: number, field: keyof User, value: string) => {
    const updatedUsers = [...users];
    updatedUsers[index][field] = value as any;
    setUsers(updatedUsers);
  };

  const removeUser = (index: number) => {
    setUsers(users.filter((_, i) => i !== index));
  };

  const isEmail = (s: string) => /\S+@\S+\.\S+/.test(s);

  const validatePayload = () => {
    if (!companyDetails.companyName?.trim()) return 'Company name is required.';
    if (!companyDetails.acl?.trim() || companyDetails.acl.trim().length !== 6) return 'ACL must be 6 characters.';
    if (!offices[0]?.name?.trim()) return 'Please enter a head office name.';
    if (isMultiOffice) {
      if (!offices[0]?.entityCode?.trim()) {
        return 'Head office entity code is required for multi-entity companies.';
      }
      for (const office of additionalOffices) {
        if (!office.name?.trim()) return 'All additional offices must have a name.';
        if (!office.entityCode?.trim()) return 'All additional offices must have an entity code.';
      }
    }
    if (!mainContact.firstName?.trim() || !mainContact.lastName?.trim()) return 'Main contact first and last name are required.';
    if (!isEmail(mainContact.email)) return 'Main contact email is invalid.';
    if (users.length === 0) return 'At least one user (Super Admin) is required.';
    if (users[0].role !== 'super_admin') return 'First user must be Super Admin.';
    for (const u of users) {
      if (!u.firstName?.trim() || !u.lastName?.trim()) return 'All users must have first and last names.';
      if (!isEmail(u.email)) return `Invalid user email: ${u.email}`;
    }
    if (!selectedTemplateId) return 'Please select a file template.';
    // Check payment if manual payment is enabled
    if (paymentConfig.isManualPayment && !paymentConfig.paymentReceived) {
      return 'Payment must be marked as received to proceed.';
    }
    return null;
  };

  const buildOnboardingPayload = (
    fullPath: string,
    zipFile: File
  ): OnboardPayloadDTO => {
    // DEBUG: Log entity codes before building payload
    console.log('[WIZARD DEBUG] Building payload with:', {
      offices: offices,
      additionalOffices: additionalOffices,
      isMultiOffice: isMultiOffice,
      headOfficeEntityCode: offices[0]?.entityCode,
      additionalEntityCodes: additionalOffices.map(o => o.entityCode)
    });
    return {
      templateId: selectedTemplateId,
      companyData: {
        companyName: companyDetails.companyName,
        acl: companyDetails.acl,
        website: companyDetails.website || undefined,
        phone: companyDetails.phone || undefined,
        address: companyDetails.address || undefined,
        // Add payment/subscription only if manual payment is enabled
        ...(paymentConfig.isManualPayment && {
          onboarding: {
            paymentReceived: paymentConfig.paymentReceived,
            paymentMethod: paymentConfig.paymentMethod,
            onboardingFee: paymentConfig.onboardingFee,
            ...(paymentConfig.invoiceNumber && { invoiceNumber: paymentConfig.invoiceNumber }),          },
          subscription: {
            plan: paymentConfig.subscriptionPlan,
            amount: paymentConfig.subscriptionPlan === 'annual' ? 499900 : 49900, // in cents
            currency: 'AUD' as const,
          }
        })
      },
      officeData: [
        {
          name: offices[0]?.name || companyDetails.companyName + ' HQ',
          isHeadOffice: true,
          entityCode: offices[0]?.entityCode?.trim() || ''
        },
        ...additionalOffices.map(o => ({
          name: o.name,
          isHeadOffice: false,
          entityCode: o.entityCode?.trim() || ''
        }))
      ],
      userData: users.map(u => ({
        email: u.email,
        displayName: `${u.firstName} ${u.lastName}`.trim(),
        role: u.role,
      })),
      fileMeta: {
        fileName: zipFile.name,
        mimeType: zipFile.type,
        storagePath: fullPath,
      },
      mainContact: {
        firstName: mainContact.firstName,
        lastName: mainContact.lastName,
        email: mainContact.email,
        mobile: mainContact.mobile || undefined,
      }
    };
  };

  const handleFinalSubmit = async () => {
    setLoading("Validating...");
    const err = validatePayload();
    if (err) {
      setError(err);
      setLoading(null);
      return;
    }

    if (!zipFile) {
      setLoading("Skipping file upload...");
      const payload = buildOnboardingPayload("", new File([], ""));
      try {
        const onboardCompany = httpsCallable(functions, 'onboardCompany');
        const result = await onboardCompany(payload);
        const data = result.data as any;
        setSuccess(`Company ${data.companyId} onboarded successfully!`);
        setLoading(null);
        toast.success('Company onboarded successfully!');
        setTimeout(() => {
          navigate(`/companies/onboarding-status/${data.companyId}`);
        }, 1500);
      } catch (e: any) {
        console.error("Onboarding error:", e);
        setError(e.message || "Failed to onboard company.");
        setLoading(null);
      }
      return;
    }

    if (!(await isZipFile(zipFile))) {
      setError("Invalid file: Not a valid ZIP archive.");
      setLoading(null);
      return;
    }

    setLoading("Uploading ZIP file...");
    const fileId = uuidv4();
    const fullPath = `pending-uploads/${fileId}/${zipFile.name}`;
    const storageRef = ref(storage, fullPath);
    const uploadTask = uploadBytesResumable(storageRef, zipFile);

    uploadTask.on('state_changed',
      (snapshot) => {
        const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
        setLoading(`Uploading... ${progress}%`);
      },
      (error) => {
        console.error("Upload error:", error);
        setError("Failed to upload file. Please try again.");
        setLoading(null);
      },
      async () => {
        setLoading("Creating company...");
        const payload = buildOnboardingPayload(fullPath, zipFile);
        try {
          const onboardCompany = httpsCallable(functions, 'onboardCompany');
          const result = await onboardCompany(payload);
          const data = result.data as any;
          setSuccess(`Company ${data.companyId} onboarded successfully!`);
          setLoading(null);
          toast.success('Company onboarded successfully!');
          setTimeout(() => {
            navigate(`/companies/onboarding-status/${data.companyId}`);
          }, 1500);
        } catch (e: any) {
          console.error("Onboarding error:", e);
          setError(e.message || "Failed to onboard company.");
          setLoading(null);
        }
      }
    );
  };

  // Handler for OnboardingDataUploader file selection
  const handleDataUploaderFileChange = (data: {
    zipFile?: File | null;
    rctiFile?: File | null;
    csvFile?: File | null;
    templateId?: string;
    uploadMode?: UploadMode;
  }) => {
    if (data.zipFile !== undefined) setZipFile(data.zipFile);
    if (data.rctiFile !== undefined) setRctiFile(data.rctiFile);
    if (data.csvFile !== undefined) setCsvFile(data.csvFile);
    if (data.templateId) setSelectedTemplateId(data.templateId);
    if (data.uploadMode) setUploadMode(data.uploadMode);
  };

  // Style classes
  const inputClasses = "w-full px-4 py-3 bg-slate-800/50 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500";
  const buttonClasses = "px-6 py-3 bg-gradient-to-r from-indigo-600 to-fuchsia-600 text-white rounded-lg hover:from-indigo-700 hover:to-fuchsia-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed";
  const backButtonClasses = "px-6 py-3 bg-slate-700/50 text-white rounded-lg hover:bg-slate-600/50 transition-all";

  return (
    <div className="bg-black/30 backdrop-blur-xl border border-white/10 rounded-2xl p-8 shadow-2xl">
      {/* Progress Indicator */}
      <div className="flex items-center justify-between mb-8">
        <h2 className="text-2xl font-bold text-white">Company Onboarding Wizard</h2>
        <div className="flex items-center gap-2">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
              i === step ? 'bg-gradient-to-r from-indigo-600 to-fuchsia-600 text-white scale-110' :
              i < step ? 'bg-green-600 text-white' : 'bg-slate-700 text-slate-400'
            }`}>
              {i < step ? '✓' : i}
            </div>
          ))}
        </div>
      </div>

      <p className="text-slate-300 mb-6">
        Step {step} of 4: {
          step === 1 ? 'Company Details' :
          step === 2 ? 'Users & Access' :
          step === 3 ? 'Payment & Data Migration' :
          'Review & Launch'
        }
      </p>

      {/* Error/Success Messages */}
      {error && (
        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-6 p-4 bg-green-500/10 border border-green-500/30 rounded-lg text-green-400">
          {success}
        </div>
      )}

      {/* Step 1: Company Details */}
      {step === 1 && (
        <form onSubmit={(e) => { e.preventDefault(); handleNextStep(); }}>
          <h3 className="text-lg font-semibold mb-4">Company Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
            <div>
              <label htmlFor="companyName" className="block text-sm font-medium text-slate-300 mb-1">
                Company Name *
              </label>
              <input 
                id="companyName" 
                type="text" 
                required 
                className={inputClasses} 
                value={companyDetails.companyName} 
                onChange={e => setCompanyDetails({...companyDetails, companyName: e.target.value})} 
              />
            </div>
            <div>
              <label htmlFor="acl" className="block text-sm font-medium text-slate-300 mb-1">
                Australian Credit Licence (ACL) *
              </label>
              <input 
                id="acl" 
                type="text" 
                required 
                maxLength={6} 
                className={inputClasses} 
                value={companyDetails.acl} 
                onChange={e => setCompanyDetails({...companyDetails, acl: e.target.value})} 
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Head Office Name *
              </label>
              <input
                type="text"
                value={offices[0]?.name || ''}
                onChange={(e) => {
                  const updated = [...offices];
                  updated[0] = { ...updated[0], name: e.target.value };
                  setOffices(updated);
                }}
                placeholder="e.g., Toorak, Melbourne CBD, Sydney"
                className={inputClasses}
                required
              />
            </div>

            {/* Multi-Office Toggle */}
            <div className="md:col-span-2 mt-4 p-4 bg-slate-800/30 rounded-lg border border-slate-700">
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-slate-300">
                    Multi-Entity Company
                  </label>
                  <p className="text-xs text-slate-500 mt-1">
                    Enable if this company receives commission files from multiple legal entities
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={isMultiOffice}
                  onChange={(e) => setIsMultiOffice(e.target.checked)}
                  className="w-5 h-5 rounded border-slate-600 bg-slate-700 text-indigo-500"
                />
              </div>

              {isMultiOffice && (
                <div className="mt-4 space-y-4">
                  <div className="p-3 bg-indigo-900/20 border border-indigo-700/30 rounded-lg">
                    <p className="text-sm text-indigo-300">
                      Configure entity codes for each office. Files will be automatically routed based on the entity prefix in filenames (e.g., ENTO100, ENTO101).
                    </p>
                  </div>

                  {/* Head Office Entity Code */}
                  <div>
                    <label className="block text-sm text-slate-400 mb-1">
                      Head Office Entity Code
                    </label>
                    <input
                      type="text"
                      placeholder="e.g., ENTO100"
                      value={offices[0]?.entityCode || ''}
                      className={inputClasses}
                      onChange={(e) => {
                        const updated = [...offices];
                        updated[0] = { ...updated[0], entityCode: e.target.value.toUpperCase() };
                        setOffices(updated);
                      }}
                    />
                  </div>

                  {/* Additional Offices */}
                  <div className="space-y-3">
                    <label className="block text-sm font-medium text-slate-300">
                      Additional Offices
                    </label>
                    {additionalOffices.map((office, index) => (
                      <div key={index} className="p-3 bg-slate-800/30 rounded-lg border border-slate-700">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs text-slate-400 mb-1">Office Name *</label>
                            <input
                              type="text"
                              placeholder="e.g., Mornington"
                              value={office.name}
                              onChange={(e) => {
                                const updated = [...additionalOffices];
                                updated[index].name = e.target.value;
                                setAdditionalOffices(updated);
                              }}
                              className={inputClasses}
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-slate-400 mb-1">Entity Code *</label>
                            <div className="flex gap-2">
                              <input
                                type="text"
                                placeholder="e.g., ENTO101"
                                value={office.entityCode}
                                onChange={(e) => {
                                  const updated = [...additionalOffices];
                                  updated[index].entityCode = e.target.value.toUpperCase();
                                  setAdditionalOffices(updated);
                                }}
                                className={inputClasses}
                              />
                              <button
                                onClick={() => {
                                  setAdditionalOffices(additionalOffices.filter((_, i) => i !== index));
                                }}
                                className="px-3 py-2 bg-red-600/50 text-white rounded-md hover:bg-red-600/80"
                                title="Remove office"
                              >
                                ×
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                    <button
                      onClick={() => {
                        setAdditionalOffices([
                          ...additionalOffices,
                          { name: '', entityCode: '', isHeadOffice: false }
                        ]);
                      }}
                      className="text-sm text-indigo-400 hover:text-indigo-300"
                    >
                      + Add Another Office
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div>
              <label htmlFor="website" className="block text-sm font-medium text-slate-300 mb-1">
                Website
              </label>
              <input
                id="website"
                type="url"
                className={inputClasses}
                value={companyDetails.website}
                onChange={e => setCompanyDetails({...companyDetails, website: e.target.value})}
              />
            </div>
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-slate-300 mb-1">
                Phone Number
              </label>
              <input 
                id="phone" 
                type="tel" 
                className={inputClasses} 
                value={companyDetails.phone} 
                onChange={e => setCompanyDetails({...companyDetails, phone: e.target.value})} 
              />
            </div>
            <div className="md:col-span-2">
              <label htmlFor="address" className="block text-sm font-medium text-slate-300 mb-1">
                Address
              </label>
              <input 
                id="address" 
                type="text" 
                autoComplete="street-address" 
                className={inputClasses} 
                value={companyDetails.address} 
                onChange={e => setCompanyDetails({...companyDetails, address: e.target.value})} 
              />
            </div>
          </div>

          <h3 className="text-lg font-semibold mb-4 border-t border-slate-700 pt-6">Main Contact</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
            <div>
              <label htmlFor="contactFirstName" className="block text-sm font-medium text-slate-300 mb-1">
                First Name *
              </label>
              <input 
                id="contactFirstName" 
                type="text" 
                required 
                className={inputClasses} 
                value={mainContact.firstName} 
                onChange={e => setMainContact({...mainContact, firstName: e.target.value})} 
              />
            </div>
            <div>
              <label htmlFor="contactLastName" className="block text-sm font-medium text-slate-300 mb-1">
                Last Name *
              </label>
              <input 
                id="contactLastName" 
                type="text" 
                required 
                className={inputClasses} 
                value={mainContact.lastName} 
                onChange={e => setMainContact({...mainContact, lastName: e.target.value})} 
              />
            </div>
            <div>
              <label htmlFor="contactEmail" className="block text-sm font-medium text-slate-300 mb-1">
                Email *
              </label>
              <input 
                id="contactEmail" 
                type="email" 
                required 
                className={inputClasses} 
                value={mainContact.email} 
                onChange={e => setMainContact({...mainContact, email: e.target.value})} 
              />
            </div>
            <div>
              <label htmlFor="contactMobile" className="block text-sm font-medium text-slate-300 mb-1">
                Mobile
              </label>
              <input 
                id="contactMobile" 
                type="tel" 
                className={inputClasses} 
                value={mainContact.mobile} 
                onChange={e => setMainContact({...mainContact, mobile: e.target.value})} 
              />
            </div>
          </div>
          <div className="mt-8 text-right">
            <button type="submit" className={buttonClasses}>Next</button>
          </div>
        </form>
      )}

      {/* Step 2: Users & Access */}
      {step === 2 && (
        <div>
          <p className="text-slate-400 text-sm mb-4">
            The first user is the company <span className="font-bold text-white">Super Admin</span>. Add other team members as needed.
          </p>
          {users.length === 0 && (
            <div className="mb-4">
              <button 
                onClick={() => setUsers([{ 
                  firstName: mainContact.firstName || '', 
                  lastName: mainContact.lastName || '', 
                  email: mainContact.email || '', 
                  role: 'super_admin' 
                }])} 
                className="w-full py-3 border-2 border-dashed border-slate-600 rounded-lg text-slate-400 hover:border-indigo-500 hover:text-indigo-400 transition-all"
              >
                + Click to add the Super Admin user
              </button>
            </div>
          )}
          <div className="space-y-4 mb-6">
            {users.map((user, index) => (
              <div key={index} className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                <div className="space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">First Name</label>
                      <input 
                        type="text" 
                        placeholder="First Name" 
                        value={user.firstName} 
                        onChange={e => updateUser(index, 'firstName', e.target.value)} 
                        required 
                        className={inputClasses} 
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Last Name</label>
                      <input 
                        type="text" 
                        placeholder="Last Name" 
                        value={user.lastName} 
                        onChange={e => updateUser(index, 'lastName', e.target.value)} 
                        required 
                        className={inputClasses} 
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="md:col-span-2">
                      <label className="block text-xs text-slate-400 mb-1">Email Address</label>
                      <input 
                        type="email" 
                        placeholder="email@company.com" 
                        value={user.email} 
                        onChange={e => updateUser(index, 'email', e.target.value)} 
                        required 
                        className={inputClasses} 
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Role</label>
                      <div className="flex gap-2">
                        <select 
                          value={user.role} 
                          disabled={index === 0} 
                          onChange={e => updateUser(index, 'role', e.target.value)} 
                          className={`${inputClasses} flex-grow disabled:opacity-70 disabled:cursor-not-allowed`}
                        >
                          <option value="super_admin">Super Admin</option>
                          <option value="administrator">Administrator</option>
                          <option value="office_admin">Office Admin</option>
                          <option value="broker">Broker</option>
                        </select>
                        {users.length > 1 && (
                          <button 
                            onClick={() => removeUser(index)} 
                            className="px-3 py-2 bg-red-600/50 text-white rounded-md hover:bg-red-600/80"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <button onClick={addUser} className="text-sm font-semibold text-indigo-400 hover:text-indigo-300">
            + Add Another User
          </button>
          <div className="mt-6 flex justify-between">
            <button onClick={handlePrevStep} className={backButtonClasses}>Back</button>
            <button onClick={handleNextStep} disabled={users.length === 0} className={buttonClasses}>Next</button>
          </div>
        </div>
      )}

      {/* Step 3: Payment & Data Migration */}
      {step === 3 && (
        <div>
          <p className="text-slate-400 text-sm mb-4">Configure payment and upload commission data.</p>
          
          {/* Payment Configuration Section */}
          <div className="mb-6 p-4 bg-slate-800/50 border border-slate-700 rounded-lg">
            <h3 className="text-lg font-semibold mb-4 text-white flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-indigo-400" />
              Payment Configuration
            </h3>
            
            <div className="space-y-4">
              {/* Manual Payment Toggle */}
              <div className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg">
                <div>
                  <label className="text-sm font-medium text-slate-300">Manual Payment Entry</label>
                  <p className="text-xs text-slate-500 mt-1">Enable for direct signup through admin</p>
                </div>
                <input
                  type="checkbox"
                  checked={paymentConfig.isManualPayment}
                  onChange={(e) => setPaymentConfig({...paymentConfig, isManualPayment: e.target.checked})}
                  className="w-5 h-5 rounded border-slate-600 bg-slate-700 text-indigo-500 focus:ring-indigo-500 focus:ring-offset-0 focus:ring-offset-slate-800"
                />
              </div>
              
              {paymentConfig.isManualPayment && (
                <div className="space-y-4 pl-4 border-l-2 border-indigo-500/30">
                  {/* Payment Received Checkbox - CRITICAL */}
                  <div className="flex items-center gap-3 p-3 bg-green-900/20 rounded-lg border border-green-800/30">
                    <input
                      type="checkbox"
                      id="paymentReceived"
                      checked={paymentConfig.paymentReceived}
                      onChange={(e) => setPaymentConfig({...paymentConfig, paymentReceived: e.target.checked})}
                      className="w-5 h-5 rounded border-green-600 bg-slate-700 text-green-500 focus:ring-green-500"
                    />
                    <label htmlFor="paymentReceived" className="text-sm font-medium text-green-400 cursor-pointer">
                      ✓ Payment Received (Required for file processing)
                    </label>
                  </div>

                  {/* Subscription Plan */}
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1">
                      Subscription Plan *
                    </label>
                    <select
                      value={paymentConfig.subscriptionPlan}
                      onChange={(e) => setPaymentConfig({
                        ...paymentConfig, 
                        subscriptionPlan: e.target.value as 'monthly' | 'annual'
                      })}
                      className={inputClasses}
                    >
                      <option value="monthly">Monthly - $499/month</option>
                      <option value="annual">Annual - $4,999/year (Save $998)</option>
                    </select>
                  </div>
                  
                  {/* Onboarding Fee */}
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1">
                      Onboarding Fee (AUD) *
                    </label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={(paymentConfig.onboardingFee / 100).toFixed(2)}
                        onChange={(e) => setPaymentConfig({
                          ...paymentConfig,
                          onboardingFee: Math.round(parseFloat(e.target.value) * 100) || 0
                        })}
                        className={`${inputClasses} pl-10`}
                        placeholder="1500.00"
                      />
                    </div>
                    <p className="text-xs text-slate-500 mt-1">Standard fee: $1,500</p>
                  </div>
                  
                  {/* Payment Method */}
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1">
                      Payment Method
                    </label>
                    <select
                      value={paymentConfig.paymentMethod}
                      onChange={(e) => setPaymentConfig({
                        ...paymentConfig, 
                        paymentMethod: e.target.value as 'manual' | 'stripe' | 'bank_transfer'
                      })}
                      className={inputClasses}
                    >
                      <option value="bank_transfer">Bank Transfer</option>
                      <option value="manual">Manual (Cash/Cheque)</option>
                      <option value="stripe" disabled>Stripe (Coming Soon)</option>
                    </select>
                  </div>
                  
                  {/* Invoice Number */}
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1">
                      Invoice Number (Optional)
                    </label>
                    <input
                      type="text"
                      value={paymentConfig.invoiceNumber}
                      onChange={(e) => setPaymentConfig({...paymentConfig, invoiceNumber: e.target.value})}
                      className={inputClasses}
                      placeholder="INV-2025-001"
                    />
                  </div>
                  
                  {/* Payment Status Warning */}
                  {!paymentConfig.paymentReceived && (
                    <div className="p-3 bg-yellow-900/20 border border-yellow-700/50 rounded-lg">
                      <p className="text-sm text-yellow-400 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" />
                        File processing will be disabled until payment is marked as received
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* File Upload Section - Unified for all templates */}
          <OnboardingDataUploader
            isOnboarding={true}
            preselectedTemplateId={selectedTemplateId}
            onFileSelected={handleDataUploaderFileChange}
            onboardingEntityMappings={
              isMultiOffice
                ? [
                    ...(offices[0]?.entityCode ? [{ code: offices[0].entityCode, officeName: offices[0].name }] : []),
                    ...additionalOffices
                      .filter(o => o.entityCode?.trim())
                      .map(o => ({ code: o.entityCode, officeName: o.name }))
                  ]
                : undefined
            }
          />
          
          <div className="mt-6 flex justify-between">
            <button onClick={handlePrevStep} className={backButtonClasses}>Back</button>
            <button onClick={handleNextStep} className={buttonClasses}>Next</button>
          </div>
        </div>
      )}

      {/* Step 4: Review & Launch */}
      {step === 4 && (
        <div>
          <h3 className="text-xl font-semibold mb-4">Review & Launch</h3>
          <div className="p-4 bg-slate-900/50 border border-slate-700 rounded-lg space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <span className="text-slate-400 text-sm">Company:</span>
                <p className="font-semibold">{companyDetails.companyName}</p>
                <p className="text-sm text-slate-500">ACL: {companyDetails.acl}</p>
              </div>
              <div>
                <span className="text-slate-400 text-sm">Main Contact:</span>
                <p className="font-semibold">{mainContact.firstName} {mainContact.lastName}</p>
                <p className="text-sm text-slate-500">{mainContact.email}</p>
              </div>
            </div>
            <div className="border-t border-slate-700 pt-3">
              <span className="text-slate-400 text-sm">Users to Create:</span>
              <div className="mt-2 space-y-1">
                {users.map((user, index) => (
                  <div key={index} className="flex justify-between text-sm">
                    <span>{user.firstName} {user.lastName}</span>
                    <span className="text-slate-400">{user.email}</span>
                    <span className="text-indigo-400">{user.role.replace('_', ' ')}</span>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Payment Summary */}
            {paymentConfig.isManualPayment && (
              <div className="border-t border-slate-700 pt-3">
                <span className="text-slate-400 text-sm">Payment Details:</span>
                <div className="mt-2 space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span>Subscription:</span>
                    <span className="text-indigo-400">
                      {paymentConfig.subscriptionPlan === 'annual' ? 'Annual ($4,999)' : 'Monthly ($499)'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Onboarding Fee:</span>
                    <span className="text-indigo-400">${(paymentConfig.onboardingFee / 100).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Payment Status:</span>
                    <span className={paymentConfig.paymentReceived ? 'text-green-400' : 'text-yellow-400'}>
                      {paymentConfig.paymentReceived ? '✓ Received' : '⚠️ Pending'}
                    </span>
                  </div>
                  {paymentConfig.invoiceNumber && (
                    <div className="flex justify-between">
                      <span>Invoice:</span>
                      <span className="text-indigo-400">{paymentConfig.invoiceNumber}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            <div className="border-t border-slate-700 pt-3 grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-slate-400">Template:</span>
                <p>{fileTemplates.find(t => t.id === selectedTemplateId)?.templateName || (selectedTemplateId ? 'Unknown template' : 'Not selected')}</p>
              </div>
              <div>
                <span className="text-slate-400">Upload Mode:</span>
                <p className="capitalize">{uploadMode === 'paired' ? 'RCTI + CSV (Paired)' : uploadMode === 'zipWithPairs' ? 'ZIP with RCTI+CSV Pairs' : 'ZIP Archive (Legacy)'}</p>
              </div>
            </div>
            <div className="border-t border-slate-700 pt-3 text-sm">
              <span className="text-slate-400">Data Files:</span>
              {uploadMode === 'paired' ? (
                <div className="mt-2 space-y-1">
                  {rctiFile && (
                    <div className="flex justify-between">
                      <span>📄 RCTI:</span>
                      <span className="text-indigo-400">{rctiFile.name}</span>
                    </div>
                  )}
                  {csvFile && (
                    <div className="flex justify-between">
                      <span>📊 CSV:</span>
                      <span className="text-indigo-400">{csvFile.name}</span>
                    </div>
                  )}
                  {!rctiFile && !csvFile && <p className="text-slate-500">No files selected</p>}
                </div>
              ) : (
                <div className="mt-2">
                  {zipFile ? (
                    <>
                      <p>{zipFile.name}</p>
                      <p className="text-xs text-slate-500">{(zipFile.size / 1024 / 1024).toFixed(2)} MB</p>
                    </>
                  ) : (
                    <p className="text-slate-500">No file selected</p>
                  )}
                </div>
              )}
            </div>
          </div>
          
          {/* Warning if payment not received */}
          {paymentConfig.isManualPayment && !paymentConfig.paymentReceived && (
            <div className="mt-4 p-3 bg-yellow-900/20 border border-yellow-700/50 rounded-lg">
              <p className="text-sm text-yellow-400 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                Warning: Payment not marked as received. File processing will be disabled.
              </p>
            </div>
          )}
          
          <div className="mt-6 flex justify-between">
            <button onClick={handlePrevStep} disabled={!!loading} className={backButtonClasses}>
              Back
            </button>
            <button 
              onClick={handleFinalSubmit} 
              disabled={!!loading || users.length === 0} 
              className={`${buttonClasses} bg-fuchsia-600 hover:bg-fuchsia-700`}
            >
              {loading ? loading : 'Complete Onboarding'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyOnboardingWizard;